/**
 * OnboardingScreenEditorDialogV2 — V2-native fullscreen editor dialog
 *
 * Fork of OnboardingScreenEditorDialog adapted for V2 StepNode schema.
 * Key differences:
 * - Input: FlowStepNode (not Screen)
 * - State: editedStep (not editedScreen)
 * - Preview: V2ScreenView renders StepNode natively (no V1 conversion)
 * - Change handling: path-based V2-native section updates
 * - Save: onStepSaved callback (not onScreenSaved)
 *
 * Layout identical to V1: Left panel (preview + device + QR) + Right panel (editor)
 *
 * Sprint: schema-v2-migration, Sprint 3
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Snackbar, Alert, useTheme, Typography, FormControl, Select, MenuItem, IconButton, Button, Tooltip, ToggleButtonGroup, ToggleButton, Portal, Chip, Divider, ListSubheader, TextField, type PaperProps } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { QRCodeSVG } from 'qrcode.react';
import {
  BaseDialog,
  UnsavedChangesDialog,
  WithGuideHelp,
  useChangeDetection,
} from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import { SupportLocationProvider } from '@modules/guides/support-location/SupportLocationContext';
import { V2ScreenView, AutoScaleScreenContainer, applyStepPresentationOverrides, PreviewOptionsRandomizationProvider, ArticleIndexPreviewContext, createArticleIndexPreview, ARTICLE_INDEX_PREVIEW_CSS } from '@modules/screen-view-v2';
import { useArticleIndexOptions } from '../../hooks/useArticleIndexOptions';
import { PresentationProfileProvider, type EditorPresentationProfile } from './editorSectionsV2/PresentationProfileContext';
import { DeviceMockup } from '@modules/device-mockup';
import { ScreenControlPanel } from '@modules/reactFlow/components/shared/ScreenControlPanel';
import { EditorToolbar } from '@modules/reactFlow/components/shared/EditorToolbar';
import {
  setNestedValue,
  deleteNestedValue,
  routeTypographyOverridePath,
  routeDefaultLayoutOverridePath,
  pruneEmptyPresentationOverrides,
} from '../../utils/v2FieldOperations';
import {
  applyAiAssetMirrorOpToStep,
  collectAiAssetMirrorOps,
  mergeAiAssetsForEditor,
  type CollectAiAssetMirrorOpsOptions,
} from '../../utils/aiAssetSlotPaths';
import { CanvasTasksPanel } from '../shared/CanvasTasksPanel';
import {
  LAYOUT_EDITOR_PANEL_WIDTH,
} from '../../config/ui';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { FlowDocument, ScreenAnimation } from '@segmently/schema-models/v2';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { DefaultOnboardingContentEditorV2 } from './DefaultOnboardingContentEditorV2';
import { VariableEditorProvider } from '../../contexts/VariableEditorContext';
import { buildPreviewVariables } from '@modules/v2-variable-utils';
import type { FlowVariablesHost } from '../../services/shared/FlowCoordinator';
import { AgentApiClient } from '@modules/content-agent/services/AgentApiClient';
import { taskFirestoreService, TERMINAL_STATUSES } from '@modules/background-tasks';
import { auth } from '@frontend/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type {
  GenerateImageConfig,
  GenerateImageResult,
  RegenerateAssetPromptConfig,
  RegenerateAssetPromptResult,
} from './editorSectionsV2/AiAssetsSection';
import { measureAiAssetScreenContext } from '@modules/reactFlow/utils/aiAssetScreenContext';
import {
  useContainerPresetCatalog,
  type ContainerCatalogPreset,
} from '@modules/reactFlow/hooks/useContainerPresetCatalog';

const SCREEN_EDITOR_OVERVIEW_ANCHOR = GUIDE_ANCHORS.screenEditorOverview;
/** The preview's screen-ID QR code — hidden for now (owner 2026-09-29); true brings it back. */
const SHOW_PREVIEW_QR_CODE = false;
type ScreenEditorDevice = 'iphone' | 'iphone-safari' | 'android' | 'tablet' | 'laptop' | 'desktop';

/**
 * Device ↔ presentation-profile sync (article documents): every preview device
 * belongs to exactly one profile class, so the frame width and the override
 * profile being edited can never contradict each other (e.g. phone overrides
 * stretched across the 1440px desktop frame).
 */
const DEVICE_PROFILE_CLASS: Record<ScreenEditorDevice, EditorPresentationProfile> = {
  iphone: 'phone',
  'iphone-safari': 'phone',
  android: 'phone',
  tablet: 'tablet',
  laptop: 'laptop',
  desktop: 'base',
};

const PROFILE_DEFAULT_DEVICE: Record<EditorPresentationProfile, ScreenEditorDevice> = {
  base: 'desktop',
  phone: 'iphone',
  tablet: 'tablet',
  laptop: 'laptop',
};

const STANDARD_DEVICE_ITEMS: { value: ScreenEditorDevice; label: string; articleOnly?: boolean }[] = [
  { value: 'iphone', label: 'iPhone 14 Pro' },
  { value: 'iphone-safari', label: 'Safari Preview' },
  { value: 'android', label: 'Android' },
  { value: 'tablet', label: 'iPad', articleOnly: true },
  { value: 'laptop', label: 'Laptop', articleOnly: true },
  { value: 'desktop', label: 'Desktop' },
];

// ── Free-form container presets (v2.19.0, article documents only) ──────────
// Named size templates for cards/ads/presentations. Selecting one writes
// StepNode.containerSize; classic device options clear it.

interface ContainerSizePreset {
  id: string;
  label: string;
  width: number;
  height: number;
}

const CONTAINER_SIZE_PRESETS: ContainerSizePreset[] = [
  { id: 'linkedin-carousel', label: 'LinkedIn Carousel (1080×1080)', width: 1080, height: 1080 },
  { id: 'facebook-ad', label: 'Facebook Ad (1200×628)', width: 1200, height: 628 },
  { id: 'instagram-story', label: 'Instagram Story (1080×1920)', width: 1080, height: 1920 },
  { id: 'presentation-16-9', label: 'Presentation 16:9 (1920×1080)', width: 1920, height: 1080 },
  { id: 'card-4-3', label: 'Card 4:3 (1200×900)', width: 1200, height: 900 },
];

const CONTAINER_SIZE_MIN = 200;
const CONTAINER_SIZE_MAX = 3840;

const PRESENTATION_PROFILE_CHIPS: { value: EditorPresentationProfile; label: string }[] = [
  { value: 'base', label: 'Base' },
  { value: 'phone', label: 'Phone' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'laptop', label: 'Laptop' },
];

function getNestedValue(obj: unknown, path: string): unknown {
  const parts = path.split('.');
  let cur: any = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

function alreadyMirrorsGeneratedUrl(obj: unknown, path: string, generatedUrl: string): boolean {
  const existing = getNestedValue(obj, path) as { original?: string; small?: string } | undefined;
  return !!existing && (existing.original === generatedUrl || existing.small === generatedUrl);
}

function getAssetMirrorStepKind(step: FlowStepNode | undefined): string | undefined {
  const rawKind = (step as any)?.kind ?? (step as any)?.screenType;
  return rawKind ? String(rawKind) : undefined;
}

export interface OnboardingScreenEditorDialogV2Props {
  /** Whether dialog is open */
  open: boolean;

  /** Callback when dialog is closed */
  onClose: () => void;

  /** V2 StepNode data to edit */
  stepData: FlowStepNode | null;

  /** Callback when step is saved successfully */
  onStepSaved?: (step: FlowStepNode) => void | Promise<void>;

  /** Project ID for Firebase operations */
  projectId?: string;

  /** Onboarding ID for Firebase operations */
  onboardingId?: string;

  /** Version ID for Firebase operations */
  versionId?: string;

  /** Default language from version (for initial locale) */
  defaultLanguage: string;

  /** Available languages from version */
  languages: string[];

  /** Map of variableId → stepIds that bind it (for multi-screen warnings) */
  screensBindingMap?: Record<string, string[]>;

  /** Full FlowDocument for variable resolution (BFS-reachable variables at this screen) */
  flowDocument?: FlowDocument | null;

  /** Theme settings snapshot for themed defaults on element toggle ON */
  themeSettingsV2?: OnboardingThemeSettingsV2 | null;

  /** Theme screen templates available for asset prompt template selection. */

  /** Editor mode: 'full' (default) shows standard sections, 'ai' shows AI-specific sections */
  mode?: 'full' | 'ai';

  /** Strategy ID — used for AI image generation upload path and API calls. NOT the same as onboardingId. */
  strategyId?: string;

  /** Strategy block ID — used for AI image generation upload path. NOT the same as versionId. */
  strategyBlockId?: string;

  /**
   * Target platform of the onboarding — threaded down from `Onboarding.platform`
   * via `OnboardingCanvasEditorV2`. When `'web'`, the device preview defaults to
   * `'iphone-safari'` (Safari Preview) so the designer immediately sees the
   * mobile-Safari chrome their users will experience. For `'ios'` / `'android'` /
   * undefined it defaults to `'iphone'`. The user can always switch devices
   * after opening via the DEVICE dropdown.
   */
  platform?: 'ios' | 'android' | 'web';

  /** Optional initial preview device for specialized editors such as articles. */
  initialDevice?: ScreenEditorDevice;

  /**
   * Optional variable storage adapter forwarded to VariableEditorProvider.
   * Hosts without onboardingId/versionId (article drafts) pass their
   * coordinator here so variable create/update/delete persists into the
   * host document instead of flowVariablesService.
   */
  variablesAdapter?: FlowVariablesHost;

  /**
   * Optional host-provided control rendered in the editor header next to the
   * "Screen editor" caption (e.g. the article Screen|Flow mode toggle).
   * Onboarding/strategy hosts pass nothing and render byte-identical DOM.
   */
  headerAction?: React.ReactNode;
}

export const OnboardingScreenEditorDialogV2: React.FC<OnboardingScreenEditorDialogV2Props> = ({
  open,
  onClose,
  stepData,
  onStepSaved,
  projectId,
  onboardingId,
  versionId,
  defaultLanguage,
  languages,
  screensBindingMap,
  flowDocument,
  themeSettingsV2,
  mode: initialMode = 'full',
  strategyId,
  strategyBlockId,
  platform,
  initialDevice,
  variablesAdapter,
  headerAction,
}) => {
  const theme = useTheme();
  const guideService = useMemo(() => new GuideService(), []);

  // State
  const [editorMode, setEditorMode] = useState<'full' | 'ai'>(initialMode);
  const [editedStep, setEditedStep] = useState<FlowStepNode | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  // Device preview — web onboardings open on Safari Preview so the designer
  // immediately sees the mobile-Safari chrome their users will actually see.
  // Native-platform onboardings (ios/android) and unspecified onboardings use
  // the plain iPhone mockup. Lazy initializer so the default only depends on
  // the platform prop value at first render; subsequent device switches are
  // purely user-driven.
  const [selectedDevice, setSelectedDevice] = useState<ScreenEditorDevice>(
    () => initialDevice ?? (platform === 'web' ? 'iphone-safari' : 'iphone')
  );

  // Locale state
  const [locale, setLocale] = useState(defaultLanguage || 'en-US');

  // K3 (D64 gap G3): sample values so the edit preview renders @variable
  // tokens as text (defaultValue → isDefault/first option → [Name] chain).
  const previewVariables = useMemo(
    () => buildPreviewVariables(
      (flowDocument?.variables ?? {}) as Record<string, import('@segmently/schema-models/v2').FunnelVariable>,
      locale,
    ),
    [flowDocument?.variables, locale],
  );

  // Selection state for interactive preview
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [fieldValue, setFieldValue] = useState<string>('');

  // Clipboard copy feedback
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  // Animation control state
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);

  // Change detection
  const changeDetectionOptions = useMemo(() => ({
    ignoreFields: ['updatedAt'],
  }), []);
  const changeDetection = useChangeDetection<FlowStepNode>(changeDetectionOptions);

  // Ref for latest editedStep — updated synchronously in change handlers
  // so that Ctrl+S save reads the latest state (not stale closure value)
  const editedStepRef = useRef<FlowStepNode | null>(null);
  useEffect(() => { editedStepRef.current = editedStep; });
  const imageTaskSubscriptionsRef = useRef<Record<string, () => void>>({});
  const previewMeasureRootRef = useRef<HTMLDivElement | null>(null);

  // ============================================================================
  // EMBED HTML MAP (for CustomEmbed preview)
  // ============================================================================

  const embedHtmlMap = useMemo(() => {
    const sections = editedStep?.content?.flexibleLayout?.sections ?? [];
    const map: Record<string, string> = {};
    for (const s of sections) {
      const html = s.embedContent?.html;
      if (s.kind === 'CustomEmbed' && html) {
        map[s.id] = html;
      }
    }
    return map;
  }, [editedStep?.content?.flexibleLayout?.sections]);

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  // Initialize edited step from stepData prop
  useEffect(() => {
    if (!open || !stepData) return;

    const stepCopy = structuredClone(stepData);
    setEditedStep(stepCopy);
    changeDetection.startTracking(stepCopy);

    // Reset locale
    setLocale(defaultLanguage || 'en-US');

    // Reset selection and field state
    setSelectedIndices(new Set());
    setSelectedIndex(null);
    setFieldValue('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stepData, defaultLanguage]);

  // Cleanup when dialog closes
  useEffect(() => {
    if (!open) {
      changeDetection.reset();
      setEditedStep(null);
      Object.values(imageTaskSubscriptionsRef.current).forEach((unsubscribe) => unsubscribe());
      imageTaskSubscriptionsRef.current = {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ============================================================================
  // CHANGE HANDLERS
  // ============================================================================

  // ── Presentation profile mode (v2.19.0, articles only) ──
  // Declared before handleContentChange so typography writes can be routed.
  const isArticleDocument =
    ((flowDocument?.metadata as Record<string, unknown> | undefined)?.kind ?? undefined) === 'article';

  // ArticleIndex preview (C-24): folder / topic names for the placeholder note. Article documents only;
  // the provider below is the editor-only seam — published pages never get it.
  const articleIndexOptions = useArticleIndexOptions(open && isArticleDocument ? projectId : undefined, locale);
  const articleIndexPreview = useMemo(
    () => (isArticleDocument
      ? createArticleIndexPreview(flowDocument?.defaultLanguage || defaultLanguage || 'en', articleIndexOptions.folders, articleIndexOptions.topics)
      : null),
    [isArticleDocument, flowDocument?.defaultLanguage, defaultLanguage, articleIndexOptions.folders, articleIndexOptions.topics],
  );

  // Editor-level presentation profile mode. Only meaningful for articles;
  // default active viewport is Phone (product decision 2026-07-02).
  const [activeProfile, setActiveProfile] = useState<EditorPresentationProfile>(
    () => (isArticleDocument ? 'phone' : 'base'),
  );

  // The useState initializer runs before document metadata loads (the canvas
  // assembles flowDocument asynchronously), so re-apply the per-kind default
  // when the dialog opens or the article flag flips.
  useEffect(() => {
    if (!open) return;
    setActiveProfile(isArticleDocument ? 'phone' : 'base');
  }, [open, isArticleDocument]);

  /**
   * Handle V2 native section changes (path-based).
   * Sets a value at a dot-notation path within the step.
   *
   * Example: onChange('content.header.skip.content.translations.en', 'Skip')
   *
   * In article profile mode, typography style paths are redirected to
   * `appearance.presentationOverrides.{profile}.*` (base values untouched);
   * null/undefined on a redirected path removes the override key.
   */
  const handleContentChange = useCallback((path: string, value: unknown) => {
    // Typography and screen-default layout routers cover disjoint path shapes;
    // applying both in sequence redirects whichever one matches.
    const effectivePath =
      isArticleDocument && activeProfile !== 'base'
        ? routeDefaultLayoutOverridePath(
            routeTypographyOverridePath(path, activeProfile),
            activeProfile,
          )
        : path;
    setEditedStep(prev => {
      if (!prev) return prev;
      const updated = structuredClone(prev);
      if (effectivePath !== path && (value === null || value === undefined)) {
        deleteNestedValue(updated as unknown as Record<string, unknown>, effectivePath);
        pruneEmptyPresentationOverrides(
          updated as unknown as Record<string, unknown>,
          effectivePath,
        );
      } else {
        setNestedValue(updated as unknown as Record<string, unknown>, effectivePath, value);
      }
      changeDetection.updateCurrent(updated);
      return updated;
    });
  }, [changeDetection, isArticleDocument, activeProfile]);

  // Handle locale change
  const handleLocaleChange = useCallback((newLocale: string) => {
    setLocale(newLocale);
  }, []);

  // Handle screen name change
  const handleNameChange = useCallback((newName: string) => {
    let updatedStep: FlowStepNode | null = null;
    setEditedStep(prev => {
      if (!prev) return prev;
      updatedStep = { ...prev, name: newName };
      changeDetection.updateCurrent(updatedStep);
      return updatedStep;
    });
    // Sync ref immediately for Ctrl+S save
    if (updatedStep) {
      editedStepRef.current = updatedStep;
    }
  }, [changeDetection]);

  // ============================================================================
  // SAVE
  // ============================================================================

  // Uses editedStepRef to always read the latest state (avoids stale closure on Ctrl+S)
  const handleSave = useCallback(async () => {
    const currentStep = editedStepRef.current;
    if (!currentStep) return;

    setIsSaving(true);
    try {
      changeDetection.startTracking(currentStep);
      await onStepSaved?.(currentStep);
    } catch (error) {
      console.error('[V2EditorDialog] Error saving step:', error);
    } finally {
      setIsSaving(false);
    }
  }, [changeDetection, onStepSaved]);

  const handleSaveAndClose = useCallback(async () => {
    await handleSave();
    onClose();
  }, [handleSave, onClose]);

  const handleSaveOnly = useCallback(async () => {
    await handleSave();
  }, [handleSave]);

  // Ctrl+S / Cmd+S hotkey
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = async (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        await handleSave();
        setShowSaveSuccess(true);
        setTimeout(() => {
          setShowSaveSuccess(false);
          onClose();
        }, 800);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleSave, onClose]);

  // ============================================================================
  // CLOSE / UNSAVED CHANGES
  // ============================================================================

  const handleClose = useCallback(() => {
    if (changeDetection.isDirty()) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  }, [changeDetection, onClose]);

  const handleDiscardChanges = useCallback(() => {
    setShowUnsavedDialog(false);
    onClose();
  }, [onClose]);

  const handleCancelDiscard = useCallback(() => {
    setShowUnsavedDialog(false);
  }, []);

  // ============================================================================
  // QR CODE
  // ============================================================================

  const qrCodeValue = useMemo(() => {
    return [projectId, onboardingId, versionId, editedStep?.id]
      .filter(Boolean)
      .join('/');
  }, [projectId, onboardingId, versionId, editedStep?.id]);

  const handleCopyQrValue = useCallback(async () => {
    if (!qrCodeValue) return;
    try {
      await navigator.clipboard.writeText(qrCodeValue);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    } catch (err) {
      console.error('[V2EditorDialog] Failed to copy:', err);
    }
  }, [qrCodeValue]);

  // ============================================================================
  // DEVICE / ANIMATION CONTROLS
  // ============================================================================

  // ── Free-form containers (v2.19.0, articles only) ──

  const stepContainerSize = (editedStep as unknown as {
    containerSize?: { width?: number; height?: number; presetRef?: string };
  } | null)?.containerSize;
  const effectiveContainerSize =
    isArticleDocument &&
    typeof stepContainerSize?.width === 'number' &&
    typeof stepContainerSize?.height === 'number' &&
    stepContainerSize.width > 0 &&
    stepContainerSize.height > 0
      ? { width: stepContainerSize.width, height: stepContainerSize.height }
      : undefined;

  // Named presets from the admin system_platforms catalog (v2.20.0);
  // empty/unreachable catalog → built-in CONTAINER_SIZE_PRESETS fallback.
  const catalogPresets = useContainerPresetCatalog(open && isArticleDocument);

  // Custom W/H drafts — committed to the step only when within bounds
  const [customWidthDraft, setCustomWidthDraft] = useState('');
  const [customHeightDraft, setCustomHeightDraft] = useState('');

  const writeContainerSize = useCallback(
    (size: { width: number; height: number; presetRef?: string } | null) => {
      setEditedStep(prev => {
        if (!prev) return prev;
        const record = prev as unknown as Record<string, unknown>;
        let updated: FlowStepNode;
        if (size === null) {
          const { containerSize: _removed, ...rest } = record;
          updated = rest as unknown as FlowStepNode;
        } else {
          updated = { ...record, containerSize: size } as unknown as FlowStepNode;
        }
        changeDetection.updateCurrent(updated);
        editedStepRef.current = updated;
        return updated;
      });
    },
    [changeDetection],
  );

  const matchingCatalogPreset =
    effectiveContainerSize && stepContainerSize?.presetRef
      ? catalogPresets.find((p) => p.presetRef === stepContainerSize.presetRef)
      : undefined;
  const matchingPreset =
    effectiveContainerSize && !matchingCatalogPreset
      ? CONTAINER_SIZE_PRESETS.find(
          (p) => p.width === effectiveContainerSize.width && p.height === effectiveContainerSize.height,
        )
      : undefined;
  const deviceSelectValue = effectiveContainerSize
    ? (matchingCatalogPreset
        ? `container:catalog:${matchingCatalogPreset.presetRef}`
        : matchingPreset
          ? `container:${matchingPreset.id}`
          : 'container:custom')
    : selectedDevice;

  const catalogPresetsRef = useRef<ContainerCatalogPreset[]>(catalogPresets);
  catalogPresetsRef.current = catalogPresets;

  const handleDeviceChange = useCallback((event: { target: { value: string } }) => {
    const value = event.target.value;
    if (value.startsWith('container:catalog:')) {
      const presetRef = value.slice('container:catalog:'.length);
      const preset = catalogPresetsRef.current.find((p) => p.presetRef === presetRef);
      if (preset) {
        setCustomWidthDraft(String(preset.width));
        setCustomHeightDraft(String(preset.height));
        writeContainerSize({ width: preset.width, height: preset.height, presetRef: preset.presetRef });
      }
      return;
    }
    if (value.startsWith('container:')) {
      const preset = CONTAINER_SIZE_PRESETS.find((p) => `container:${p.id}` === value);
      const current = editedStepRef.current as unknown as {
        containerSize?: { width?: number; height?: number };
      } | null;
      const fallback = {
        width: current?.containerSize?.width ?? 1000,
        height: current?.containerSize?.height ?? 750,
      };
      const size = preset ? { width: preset.width, height: preset.height } : fallback;
      setCustomWidthDraft(String(size.width));
      setCustomHeightDraft(String(size.height));
      writeContainerSize(size);
      return;
    }
    writeContainerSize(null);
    setSelectedDevice(value as ScreenEditorDevice);
  }, [writeContainerSize]);

  const commitCustomDimension = useCallback((dimension: 'width' | 'height', raw: string) => {
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < CONTAINER_SIZE_MIN || parsed > CONTAINER_SIZE_MAX) return;
    const current = editedStepRef.current as unknown as {
      containerSize?: { width?: number; height?: number };
    } | null;
    // Manual dimension edits break the link to a named preset — no presetRef.
    const size = {
      width: current?.containerSize?.width ?? 1000,
      height: current?.containerSize?.height ?? 750,
      [dimension]: Math.round(parsed),
    };
    writeContainerSize(size as { width: number; height: number });
  }, [writeContainerSize]);

  const isCustomDimensionInvalid = (raw: string): boolean => {
    if (raw === '') return false;
    const parsed = Number(raw);
    return !Number.isFinite(parsed) || parsed < CONTAINER_SIZE_MIN || parsed > CONTAINER_SIZE_MAX;
  };

  // Container is an ALTERNATIVE to viewports (v2.20.0): a fixed container
  // forces base mode (chips hidden below). Otherwise the active profile is
  // DERIVED from the preview device's class — device and profile can never
  // disagree. Overrides in the data stay dormant — never deleted.
  const hasFixedContainer = Boolean(effectiveContainerSize);
  useEffect(() => {
    if (!open || !isArticleDocument) return;
    setActiveProfile(hasFixedContainer ? 'base' : DEVICE_PROFILE_CLASS[selectedDevice]);
  }, [open, isArticleDocument, hasFixedContainer, selectedDevice]);

  // Remember the last device used per profile class so chip switches return
  // to the device the user was previewing in that class (e.g. Android).
  const lastDeviceByClassRef = useRef<Partial<Record<EditorPresentationProfile, ScreenEditorDevice>>>({});
  useEffect(() => {
    lastDeviceByClassRef.current[DEVICE_PROFILE_CLASS[selectedDevice]] = selectedDevice;
  }, [selectedDevice]);

  // Chip → device: switching the profile switches the preview frame to a
  // device of that class; the derive-effect above re-asserts the profile.
  const handleProfileChipSelect = useCallback((profile: EditorPresentationProfile) => {
    setActiveProfile(profile);
    setSelectedDevice((prev) =>
      DEVICE_PROFILE_CLASS[prev] === profile
        ? prev
        : lastDeviceByClassRef.current[profile] ?? PROFILE_DEFAULT_DEVICE[profile],
    );
  }, []);

  // Preview renders the ACTIVE profile so overrides are WYSIWYG.
  // Container screens render exact BASE values (both tiers skipped).
  const previewStep = useMemo(() => {
    if (!editedStep || !isArticleDocument || activeProfile === 'base' || hasFixedContainer) {
      return editedStep;
    }
    return applyStepPresentationOverrides(editedStep, activeProfile);
  }, [editedStep, isArticleDocument, activeProfile, hasFixedContainer]);

  const showSafariBars = selectedDevice === 'iphone-safari';
  const deviceForMockup = selectedDevice === 'iphone-safari' ? 'iphone' : selectedDevice;

  const handlePreviewPlay = useCallback(() => setIsPreviewPlaying(true), []);
  const handlePreviewStop = useCallback(() => setIsPreviewPlaying(false), []);
  const handleAnimationComplete = useCallback(() => setIsPreviewPlaying(false), []);

  // Replay in-screen animations: AnimationContext replays on the RISING edge of
  // isAnimationPlaying (edit mode), so an already-playing preview must drop to
  // false for one frame before rising again. rAF-batched — rapid repeated
  // clicks (AC-027) coalesce into a clean restart instead of thrashing state.
  const replayRafRef = useRef<number | null>(null);
  const handlePreviewReplay = useCallback(() => {
    if (replayRafRef.current !== null) return; // restart already scheduled
    setIsPreviewPlaying(false);
    replayRafRef.current = requestAnimationFrame(() => {
      replayRafRef.current = null;
      setIsPreviewPlaying(true);
    });
  }, []);
  useEffect(() => () => {
    if (replayRafRef.current !== null) cancelAnimationFrame(replayRafRef.current);
  }, []);

  const measureAiAssetSlotContext = useCallback((slotKey: string) => {
    return measureAiAssetScreenContext({
      previewRoot: previewMeasureRootRef.current,
      step: editedStepRef.current,
      slotKey,
      device: selectedDevice,
    });
  }, [selectedDevice]);

  const syncGeneratedAssetFromScreenDoc = useCallback(async (
    slotKey: string,
    experimentId?: string,
  ) => {
    if (!projectId || !strategyId || !strategyBlockId) return;

    const currentStep = editedStepRef.current;
    const screenId = currentStep?.id || (currentStep as any)?.entityId;
    if (!screenId) return;

    const db = CoreFirebaseService.getInstance().firestore;
    const screenRef = doc(
      db,
      `projects/${projectId}/strategies/${strategyId}/blocks/${strategyBlockId}/screens/${screenId}`,
    );

    const snap = await getDoc(screenRef);
    const nextAiAssets = snap.data()?.aiAssets as Record<string, any> | undefined;
    if (!nextAiAssets) return;

    setEditedStep(prev => {
      if (!prev) return prev;

      const prevAiAssets = ((prev as any).aiAssets ?? {}) as Record<string, any>;
      const updated = structuredClone(prev) as any;
      const mergedAiAssets = mergeAiAssetsForEditor(prevAiAssets, nextAiAssets);
      if (experimentId && mergedAiAssets[slotKey]) {
        mergedAiAssets[slotKey] = {
          ...mergedAiAssets[slotKey],
          selectedExperimentId: experimentId,
        };
      }
      updated.aiAssets = mergedAiAssets;

      const mirrorOps = collectAiAssetMirrorOps(mergedAiAssets, locale, {
        slotKeys: [slotKey],
        experimentIdsBySlot: experimentId ? { [slotKey]: experimentId } : undefined,
        stepKind: getAssetMirrorStepKind(updated as FlowStepNode),
      }).filter((op) => !alreadyMirrorsGeneratedUrl(updated, op.path, op.generatedUrl));

      for (const op of mirrorOps) {
        applyAiAssetMirrorOpToStep(updated, op);
      }

      if (mirrorOps.length > 0) {
        changeDetection.updateCurrent(updated);
      }

      return updated as FlowStepNode;
    });
  }, [changeDetection, locale, projectId, strategyBlockId, strategyId]);

  const subscribeToImageTaskCompletion = useCallback((
    taskId: string,
    slotKey: string,
    experimentId?: string,
  ) => {
    if (!projectId || imageTaskSubscriptionsRef.current[taskId]) return;

    const unsubscribe = taskFirestoreService.subscribe(
      projectId,
      taskId,
      (task) => {
        if (!task || !TERMINAL_STATUSES.includes(task.status)) return;

        const cleanup = imageTaskSubscriptionsRef.current[taskId];
        if (cleanup) {
          cleanup();
          delete imageTaskSubscriptionsRef.current[taskId];
        }

        void syncGeneratedAssetFromScreenDoc(slotKey, experimentId);
      },
      (error) => {
        console.error('[V2EditorDialog] Image generation task listener error:', error);
      },
    );

    imageTaskSubscriptionsRef.current[taskId] = unsubscribe;
  }, [projectId, syncGeneratedAssetFromScreenDoc]);

  // AI Image Generation handler — fire-and-forget.
  // Returns taskId immediately. A task-scoped listener below performs one
  // screen refetch when that specific generation reaches a terminal status.
  const handleGenerateImage = useCallback(async (slotKey: string, config: GenerateImageConfig): Promise<GenerateImageResult> => {
    const userId = auth.currentUser?.uid;
    const screenId = editedStep?.id || editedStep?.entityId || '';

    if (!projectId || !userId || !screenId) {
      return {
        success: false,
        error: `Missing context: projectId=${projectId}, userId=${userId}, screenId=${screenId}`,
      };
    }

    try {
      const client = new AgentApiClient();
      const result = await client.generateImage({
        projectId,
        strategyId: strategyId || '',
        blockId: strategyBlockId || '',
        screenId,
        slotKey,
        experimentId: config.experimentId,
        prompt: config.prompt,
        negativePrompt: config.negativePrompt,
        aspectRatio: config.aspectRatio,
        imageSize: config.imageSize,
        openaiSize: config.openaiSize,
        quality: config.quality,
        style: config.style,
        model: config.model,
        provider: config.provider,
        referenceImages: config.referenceImages,
        userId,
      });
      if (result.success && result.taskId) {
        subscribeToImageTaskCompletion(result.taskId, slotKey, config.experimentId);
      }

      // Fire-and-forget: task listener above does a one-shot screen refetch on completion.
      return { success: result.success, taskId: result.taskId, error: result.error };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      };
    }
  }, [projectId, strategyId, strategyBlockId, editedStep, subscribeToImageTaskCompletion]);

  const handleRegenerateAssetPrompt = useCallback(async (
    slotKey: string,
    config: RegenerateAssetPromptConfig,
  ): Promise<RegenerateAssetPromptResult> => {
    const screenId = editedStepRef.current?.id || (editedStepRef.current as any)?.entityId || '';

    if (!projectId || !strategyId || !strategyBlockId || !screenId) {
      return {
        success: false,
        error: `Missing context: projectId=${projectId}, strategyId=${strategyId}, blockId=${strategyBlockId}, screenId=${screenId}`,
      };
    }

    try {
      const client = new AgentApiClient();
      const response = await client.regenerateStrategyBlockAssetSlotPrompt({
        projectId,
        strategyId,
        blockId: strategyBlockId,
        slotKey,
        screenId,
        regeneration: {
          sourceExperimentId: config.sourceExperimentId,
          promptAuthoringMode: config.promptAuthoringMode,
          experimentId: config.experimentId,
          userInstruction: config.instruction,
          model: config.model,
          conceptCount: config.conceptCount,
          renderContext: config.renderContext as unknown as Record<string, unknown> | null | undefined,
          selectRegenerated: config.selectRegenerated ?? true,
        },
      });

      if ((response as any).success === false) {
        return {
          success: false,
          error: (response as any).error || 'Failed to start prompt regeneration',
        };
      }

      const data = response.data;
      if (data.taskId) {
        subscribeToImageTaskCompletion(data.taskId, slotKey);
      }

      return {
        success: true,
        taskId: data.taskId,
        experimentId: data.experimentId,
      };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to start prompt regeneration',
      };
    }
  }, [projectId, strategyId, strategyBlockId, subscribeToImageTaskCompletion]);

  const collectUnmirroredMirrorOps = useCallback((
    step: FlowStepNode,
    options?: CollectAiAssetMirrorOpsOptions,
  ) => {
    const aiAssets = (step as any).aiAssets as Record<string, any> | undefined;
    return collectAiAssetMirrorOps(aiAssets, locale, {
      ...options,
      stepKind: getAssetMirrorStepKind(step),
    }).filter(
      (op) => !alreadyMirrorsGeneratedUrl(step, op.path, op.generatedUrl),
    );
  }, [locale]);

  const applyGeneratedAssetMirrorOps = useCallback((
    options?: CollectAiAssetMirrorOpsOptions,
  ) => {
    setEditedStep(prev => {
      if (!prev) return prev;
      const mirrorOps = collectUnmirroredMirrorOps(prev, options);
      if (mirrorOps.length === 0) return prev;

      const updated = structuredClone(prev) as any;
      for (const op of mirrorOps) {
        applyAiAssetMirrorOpToStep(updated, op);
      }
      changeDetection.updateCurrent(updated);
      console.log('[ScreenEditor] Applied generated image URLs to content paths', {
        slotPaths: mirrorOps.map(o => o.path),
      });
      return updated as FlowStepNode;
    });
  }, [changeDetection, collectUnmirroredMirrorOps]);

  const handleApplyGeneratedAsset = useCallback((slotKey: string, experimentId: string) => {
    applyGeneratedAssetMirrorOps({
      slotKeys: [slotKey],
      experimentIdsBySlot: { [slotKey]: experimentId },
    });
  }, [applyGeneratedAssetMirrorOps]);

  const handleApplyAllGeneratedAssets = useCallback(() => {
    applyGeneratedAssetMirrorOps();
  }, [applyGeneratedAssetMirrorOps]);

  // ============================================================================
  // MIRROR-ON-MOUNT — reconcile already-completed experiments on reopen
  //
  // The listener above only runs while experiments are still in `status='generating'`.
  // If the user generated an image in a previous session and reopened the screen,
  // aiAssets.*.experiments.*.status is already 'completed' and the listener never
  // activates — so content.*.translations[locale] never gets the generated URL and
  // the preview shows the old image. This one-shot effect runs on every screen
  // change and mirrors any completed experiments whose URLs haven't yet landed
  // in the content tree. No-op when content already matches, so it doesn't spam
  // the dirty-state flag.
  // ============================================================================

  useEffect(() => {
    if (!editedStep?.id) return;
    const mirrorOps = collectUnmirroredMirrorOps(editedStep);

    if (mirrorOps.length === 0) return;

    console.log('[ScreenEditor] Mirror-on-mount: reconciling completed experiments', {
      slotPaths: mirrorOps.map(o => o.path),
    });

    setEditedStep(prev => {
      if (!prev) return prev;
      const updated = structuredClone(prev) as any;
      for (const op of mirrorOps) {
        applyAiAssetMirrorOpToStep(updated, op);
      }
      changeDetection.updateCurrent(updated);
      return updated as FlowStepNode;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedStep?.id, locale, collectUnmirroredMirrorOps]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!editedStep) return null;

  return (
    <SupportLocationProvider
      value={{
        projectId,
        onboardingId,
        screenId: editedStep.entityId,
        screenName: editedStep.name,
        screenType: editedStep.screenType ? String(editedStep.screenType) : undefined,
      }}
    >
      <BaseDialog
        open={open}
        onClose={handleClose}
        fullScreen={true}
        maxWidth={false}
        sx={{ padding: 0, margin: 0 }}
        slotProps={{
          // data-testid is a passthrough DOM attribute PaperProps doesn't declare.
          paper: {
            'data-testid': 'screen-editor-dialog',
            sx: { padding: 0, margin: 0 },
          } as PaperProps,
        }}
      >
        <Box
          data-testid="editor-modal-v2-container"
          sx={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          {/* Left Panel: Control + Preview */}
          <Box
            data-testid="editor-preview-panel"
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRight: `1px solid ${theme.palette.divider}`,
            }}
          >
            {/* Top: Control Panel */}
            <ScreenControlPanel
              screenName={editedStep.name || 'Untitled Screen'}
              screenType={editedStep.screenType}
              screenId={editedStep.id}
              isSaving={isSaving}
              onClose={handleClose}
              onNameChange={handleNameChange}
              endAdornment={
                <>
                  {headerAction}
                  <WithGuideHelp
                    guideKey={SCREEN_EDITOR_OVERVIEW_ANCHOR.guideKey}
                    guideService={guideService}
                    data-testid={SCREEN_EDITOR_OVERVIEW_ANCHOR.testId}
                    position="inline-end"
                  >
                    <Typography variant="caption" color="text.secondary">
                      Screen editor
                    </Typography>
                  </WithGuideHelp>
                </>
              }
            />

            {/* Preview Panel */}
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                p: 4,
              }}
            >
              {/* Device selector + container size + animation replay */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '9px',
                      fontWeight: 500,
                      color: theme.palette.text.secondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    DEVICE
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 140, margin: 0 }}>
                    <Select
                      value={deviceSelectValue}
                      onChange={handleDeviceChange}
                      data-testid="editor-device-select"
                      sx={{
                        fontSize: '14px',
                        '& .MuiSelect-select': {
                          padding: 0,
                          paddingRight: '24px !important',
                        },
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      }}
                    >
                      {/* Standard devices: onboarding docs keep the fixed
                        * classic list; article docs in viewport mode show only
                        * the ACTIVE profile's class (device ↔ profile sync) —
                        * other classes are reached via the profile chips.
                        * Container mode lists every class as exit paths. */}
                      {STANDARD_DEVICE_ITEMS.filter((item) =>
                        !isArticleDocument
                          ? !item.articleOnly
                          : hasFixedContainer || DEVICE_PROFILE_CLASS[item.value] === activeProfile,
                      ).map((item) => (
                        <MenuItem
                          key={item.value}
                          value={item.value}
                          data-testid={`device-option-${item.value}`}
                        >
                          {item.label}
                        </MenuItem>
                      ))}
                      {/* Container size templates: article documents only.
                        * Catalog presets (admin system_platforms, grouped by
                        * platform) when available; built-ins as fallback. */}
                      {isArticleDocument && <Divider />}
                      {isArticleDocument && catalogPresets.length > 0
                        ? Array.from(
                            catalogPresets.reduce((groups, preset) => {
                              const list = groups.get(preset.platformName) ?? [];
                              list.push(preset);
                              groups.set(preset.platformName, list);
                              return groups;
                            }, new Map<string, ContainerCatalogPreset[]>()),
                          ).flatMap(([platformName, presets]) => [
                            <ListSubheader
                              key={`platform-${platformName}`}
                              sx={{ lineHeight: '28px', fontSize: '0.7rem' }}
                            >
                              {platformName}
                            </ListSubheader>,
                            ...presets.map((preset) => (
                              <MenuItem
                                key={preset.presetRef}
                                value={`container:catalog:${preset.presetRef}`}
                                data-testid={`container-preset-${preset.presetRef.replace(/\//g, '-')}`}
                              >
                                {preset.label}
                              </MenuItem>
                            )),
                          ])
                        : isArticleDocument && [
                            <ListSubheader
                              key="builtin-header"
                              sx={{ lineHeight: '28px', fontSize: '0.7rem' }}
                            >
                              Container templates
                            </ListSubheader>,
                            ...CONTAINER_SIZE_PRESETS.map((preset) => (
                              <MenuItem key={preset.id} value={`container:${preset.id}`}>
                                {preset.label}
                              </MenuItem>
                            )),
                          ]}
                      {isArticleDocument && (
                        <MenuItem value="container:custom" data-testid="device-option-custom-size">
                          Custom size…
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>

                {/* Custom W/H inputs — visible whenever a container size is active */}
                {effectiveContainerSize && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      label="W, px"
                      value={customWidthDraft || String(effectiveContainerSize.width)}
                      error={isCustomDimensionInvalid(customWidthDraft)}
                      helperText={isCustomDimensionInvalid(customWidthDraft) ? `${CONTAINER_SIZE_MIN}–${CONTAINER_SIZE_MAX}` : undefined}
                      onChange={(e) => {
                        setCustomWidthDraft(e.target.value);
                        commitCustomDimension('width', e.target.value);
                      }}
                      inputProps={{ 'data-testid': 'container-width-input', inputMode: 'numeric' }}
                      sx={{ width: 92 }}
                    />
                    <Typography variant="caption" color="text.secondary">×</Typography>
                    <TextField
                      size="small"
                      label="H, px"
                      value={customHeightDraft || String(effectiveContainerSize.height)}
                      error={isCustomDimensionInvalid(customHeightDraft)}
                      helperText={isCustomDimensionInvalid(customHeightDraft) ? `${CONTAINER_SIZE_MIN}–${CONTAINER_SIZE_MAX}` : undefined}
                      onChange={(e) => {
                        setCustomHeightDraft(e.target.value);
                        commitCustomDimension('height', e.target.value);
                      }}
                      inputProps={{ 'data-testid': 'container-height-input', inputMode: 'numeric' }}
                      sx={{ width: 92 }}
                    />
                  </Box>
                )}

                <Tooltip title="Play the screen animations once">
                  <Box component="span" sx={{ ml: 'auto' }}>
                    {/* Same one-shot control and label as the theme gallery's "Play animations"
                        (owner 2026-09-29). Re-clickable while playing: it restarts. */}
                    <Button
                      data-testid="editor-preview-replay-btn"
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={handlePreviewReplay}
                      startIcon={<PlayArrowIcon />}
                      sx={{ minWidth: 148 }}
                    >
                      {isPreviewPlaying ? 'Playing…' : 'Play animations'}
                    </Button>
                  </Box>
                </Tooltip>

                {/* Presentation profile switcher — GLOBAL editor mode (articles
                  * only). Hidden in container mode: a fixed container renders
                  * exact base values, viewport profiles do not apply. */}
                {isArticleDocument && !hasFixedContainer && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    data-testid="presentation-profile-switcher"
                  >
                    <Typography variant="caption" color="text.secondary">Profile:</Typography>
                    {PRESENTATION_PROFILE_CHIPS.map(({ value, label }) => (
                      <Chip
                        key={value}
                        size="small"
                        label={label}
                        color={activeProfile === value ? 'primary' : 'default'}
                        variant={activeProfile === value ? 'filled' : 'outlined'}
                        onClick={() => handleProfileChipSelect(value)}
                        data-testid={`profile-chip-${value}`}
                      />
                    ))}
                  </Box>
                )}
              </Box>
              {isArticleDocument && !hasFixedContainer && activeProfile !== 'base' && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                  data-testid="profile-mode-hint"
                >
                  Editing {activeProfile} overrides — changed layout/spacing fields apply only on {activeProfile} width. Base values stay intact.
                </Typography>
              )}

              {/* Preview + QR code row */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  gap: 3,
                  minHeight: 0,
                }}
              >
                {/* Device mockup with V2ScreenView — renders StepNode natively */}
                <Box
                  ref={previewMeasureRootRef}
                  data-testid="editor-preview-measure-root"
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 0,
                  }}
                >
                  {/* The preview shows what the visitor gets — the same collapse
                      past the options threshold and, for a randomized list, one
                      deterministic sample of the shuffled order rather than the
                      document order no visitor sees (R-POST-3 / R-W11-5). The
                      options EDITOR is the right-hand section, which stays in
                      document order. */}
                  <ArticleIndexPreviewContext.Provider value={articleIndexPreview}>
                  {/* Article documents: the editor has no article style block, so the ArticleIndex placeholder gets
                      its own editor-only sheet (C-24; design review 2026-10-07). */}
                  {articleIndexPreview && <style data-article-index-preview-css="">{ARTICLE_INDEX_PREVIEW_CSS}</style>}
                  <PreviewOptionsRandomizationProvider screenId={previewStep?.id ?? stepData?.entityId}>
                  {effectiveContainerSize ? (
                    // Free-form container preview (v2.19.0): exact WxH card scaled to fit
                    <AutoScaleScreenContainer
                      data-testid="container-preview"
                      scaleOptions={{
                        contentWidth: effectiveContainerSize.width,
                        contentHeight: effectiveContainerSize.height,
                        maxScale: 1.5,
                        padding: 16,
                      }}
                    >
                      <Box
                        sx={{
                          width: effectiveContainerSize.width,
                          height: effectiveContainerSize.height,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          overflow: 'auto',
                        }}
                        data-testid="container-preview-card"
                      >
                        {previewStep && (
                          <V2ScreenView
                            step={previewStep}
                            locale={locale}
                            editMode={true}
                            previewMode="edit"
                            selectedIndices={selectedIndices}
                            selectedIndex={selectedIndex}
                            onMultipleSelectionChange={setSelectedIndices}
                            onSingleSelectionChange={setSelectedIndex}
                            fieldValue={fieldValue}
                            onFieldValueChange={setFieldValue}
                            isAnimationPlaying={isPreviewPlaying}
                            onAnimationComplete={handleAnimationComplete}
                            embedHtmlMap={embedHtmlMap}
                            variables={previewVariables}
                            onEvent={(event) => {
                              console.log('[V2EditorDialog] Preview event:', event);
                            }}
                            onAction={(event) => {
                              console.log('[V2EditorDialog] Preview action:', event);
                            }}
                          />
                        )}
                      </Box>
                    </AutoScaleScreenContainer>
                  ) : (
                  <DeviceMockup
                    device={deviceForMockup}
                    data-testid="device-mockup"
                    scaleOptions={{ maxScale: 1.5, padding: 16 }}
                    showSafariBars={showSafariBars}
                  >
                    {previewStep && (
                      <V2ScreenView
                        step={previewStep}
                        locale={locale}
                        editMode={true}
                        previewMode="edit"
                        selectedIndices={selectedIndices}
                        selectedIndex={selectedIndex}
                        onMultipleSelectionChange={setSelectedIndices}
                        onSingleSelectionChange={setSelectedIndex}
                        fieldValue={fieldValue}
                        onFieldValueChange={setFieldValue}
                        isAnimationPlaying={isPreviewPlaying}
                        onAnimationComplete={handleAnimationComplete}
                        // Theme-inherited entrance: without this the preview
                        // (and Replay) is a no-op for screens whose motion
                        // comes only from the theme default
                        defaultScreenAnimation={
                          ((flowDocument as unknown as Record<string, unknown> | null | undefined)
                            ?.defaultScreenAnimation as ScreenAnimation | undefined) ?? null
                        }
                        embedHtmlMap={embedHtmlMap}
                        variables={previewVariables}
                        onEvent={(event) => {
                          console.log('[V2EditorDialog] Preview event:', event);
                        }}
                        onAction={(event) => {
                          console.log('[V2EditorDialog] Preview action:', event);
                        }}
                      />
                    )}
                  </DeviceMockup>
                  )}
                  </PreviewOptionsRandomizationProvider>
                  </ArticleIndexPreviewContext.Provider>
                </Box>

                {/* QR Code — hidden for now (owner 2026-09-29: not needed at the moment);
                    flip SHOW_PREVIEW_QR_CODE to bring it back. */}
                {SHOW_PREVIEW_QR_CODE && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    pt: 4,
                    flexShrink: 0,
                    maxWidth: 160,
                  }}
                >
                  <QRCodeSVG
                    value={qrCodeValue || 'no-data'}
                    size={100}
                    level="M"
                    includeMargin={false}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 1.5,
                      px: 1,
                      py: 0.5,
                      bgcolor: theme.palette.action.hover,
                      borderRadius: 1,
                      maxWidth: '100%',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '10px',
                        fontFamily: 'monospace',
                        color: theme.palette.text.secondary,
                        wordBreak: 'break-all',
                        lineHeight: 1.3,
                      }}
                    >
                      {qrCodeValue || 'no-data'}
                    </Typography>
                    <Tooltip title={copiedToClipboard ? 'Copied!' : 'Copy to clipboard'}>
                      <IconButton
                        size="small"
                        onClick={handleCopyQrValue}
                        sx={{
                          p: 0.5,
                          color: copiedToClipboard
                            ? theme.palette.success.main
                            : theme.palette.action.active,
                        }}
                      >
                        {copiedToClipboard ? (
                          <CheckIcon sx={{ fontSize: 14 }} />
                        ) : (
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, textAlign: 'center', fontSize: '9px' }}
                  >
                    Scan or copy for screen ID
                  </Typography>
                </Box>
                )}
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                Device: {selectedDevice === 'iphone-safari' ? 'iPhone (Safari)' : selectedDevice} | Locale: {locale}
              </Typography>
            </Box>
          </Box>

          {/* Right Panel: Editor */}
          <Box
            data-testid="editor-content-panel"
            sx={{
              width: LAYOUT_EDITOR_PANEL_WIDTH,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: theme.palette.background.paper,
            }}
          >
            {/* Editor Toolbar: Language + Save buttons */}
            <EditorToolbar
              locale={locale}
              availableLocales={languages}
              onLocaleChange={handleLocaleChange}
              hasChanges={changeDetection.isDirty()}
              isSaving={isSaving}
              onSave={handleSaveAndClose}
              onSaveOnly={handleSaveOnly}
            />

            {/* Editor Mode Toggle — only visible in AI/strategy context */}
            {initialMode === 'ai' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>View:</Typography>
                <ToggleButtonGroup
                  value={editorMode}
                  exclusive
                  onChange={(_, val) => { if (val) setEditorMode(val); }}
                  size="small"
                  sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1, fontSize: '0.7rem' } }}
                >
                  <ToggleButton value="full">
                    <ViewAgendaIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Full
                  </ToggleButton>
                  <ToggleButton value="ai">
                    <AutoAwesomeIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    AI
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Content Editor (scrollable) — V2 native */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <PresentationProfileProvider value={isArticleDocument ? activeProfile : 'base'}>
              <VariableEditorProvider key={editedStep?.entityId || editedStep?.id || 'no-step'} projectId={projectId} onboardingId={onboardingId} versionId={versionId} screensBindingMap={screensBindingMap} variablesAdapter={variablesAdapter}>
                <DefaultOnboardingContentEditorV2
                  step={editedStep}
                  onContentChange={handleContentChange}
                  isSaving={isSaving}
                  locale={locale}
                  availableLocales={languages}
                  defaultLanguage={defaultLanguage}
                  languages={languages}
                  projectId={projectId}
                  onboardingId={onboardingId}
                  versionId={versionId}
                  onPreviewPlay={handlePreviewPlay}
                  onPreviewStop={handlePreviewStop}
                  isPreviewPlaying={isPreviewPlaying}
                  onAnimationSettingsChange={handlePreviewReplay}
                  flowDocument={flowDocument}
                  themeSettingsV2={themeSettingsV2}
                  mode={editorMode}
                  onGenerateImage={handleGenerateImage}
                  onRegenerateAssetPrompt={handleRegenerateAssetPrompt}
                  measureAiAssetSlotContext={measureAiAssetSlotContext}
                  onApplyGeneratedAsset={handleApplyGeneratedAsset}
                  onApplyAllGeneratedAssets={handleApplyAllGeneratedAssets}
                  strategyId={strategyId}
                  strategyBlockId={strategyBlockId}
                  /*
                   * M7: thread the editor's save handler + dirty state down
                   * to V2PaywallSubscriptionsSection → SubscriptionItemEditor
                   * → PaywallProductPickerControl. The picker uses these to
                   * offer "Save and connect Stripe" before redirecting to OAuth.
                   */
                  onSaveScreen={handleSave}
                  hasUnsavedChanges={changeDetection.isDirty()}
                />
              </VariableEditorProvider>
              </PresentationProfileProvider>
            </Box>
          </Box>
        </Box>
      </BaseDialog>

      {/* In-dialog tasks panel — image-generation tasks scoped to this screen.
          Wrapped in Portal so the DOM node lands in document.body alongside the
          MUI Dialog Portal, avoiding stacking-context issues. zIndex 1400 sits
          above the MUI modal layer (1300). Only visible in AI editor mode. */}
      {projectId && editedStep?.id && editorMode === 'ai' && (
        <Portal>
          <CanvasTasksPanel
            projectId={projectId}
            strategyId={strategyId}
            blockId={strategyBlockId}
            screenId={editedStep.id}
            taskType={['generate-image', 'strategy-asset-prompt-regeneration']}
            zIndex={1400}
          />
        </Portal>
      )}

      {/* Unsaved Changes Confirmation */}
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        changedFields={changeDetection.getChanges()}
        onCancel={handleCancelDiscard}
        onDiscard={handleDiscardChanges}
        onSave={async () => {
          await handleSave();
          setShowUnsavedDialog(false);
          onClose();
        }}
        saving={isSaving}
      />

      {/* Save success toast (Ctrl+S) */}
      <Snackbar
        open={showSaveSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled">
          Step saved
        </Alert>
      </Snackbar>
    </SupportLocationProvider>
  );
};
